import React, { useEffect, useState } from 'react';
import { Chip } from '../../lib/types';
import { mapChipToAdopterForm, mapChipToPetForm } from '../../lib/chipAdapters';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import PetFormFields, { INITIAL_PET, PetFormData, validatePetForm } from './PetFormFields';
import OwnerFormFields, { AdopterFormData, INITIAL_ADOPTER, validateAdopterForm } from './OwnerFormFields';
import styles from './Chips.module.scss';

export interface EditChipPayload {
  originalPet: PetFormData;
  pet: PetFormData | null;
  adopter: AdopterFormData | null;
}

interface EditChipModalProps {
  open: boolean;
  onClose: () => void;
  chip: Chip | null;
  canEditPet: boolean;
  canEditOwner: boolean;
  onSave: (chip: Chip, payload: EditChipPayload) => Promise<void>;
}

export default function EditChipModal({ open, onClose, chip, canEditPet, canEditOwner, onSave }: EditChipModalProps) {
  const [originalPet, setOriginalPet] = useState<PetFormData>({ ...INITIAL_PET });
  const [pet, setPet] = useState<PetFormData>({ ...INITIAL_PET });
  const [petErrors, setPetErrors] = useState<Record<string, boolean>>({});
  const [adopter, setAdopter] = useState<AdopterFormData>({ ...INITIAL_ADOPTER });
  const [adopterErrors, setAdopterErrors] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && chip) {
      const petForm = mapChipToPetForm(chip);
      setOriginalPet(petForm);
      setPet(petForm);
      setAdopter(mapChipToAdopterForm(chip));
      setPetErrors({});
      setAdopterErrors({});
      setSubmitting(false);
    }
    // Only seed the form when the modal opens, not on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!chip) {
    return null;
  }

  function updatePet<K extends keyof PetFormData>(key: K, value: PetFormData[K]) {
    setPet((prev) => ({ ...prev, [key]: value }));
    setPetErrors((prev) => ({ ...prev, [key]: false }));
  }

  function updateAdopter(key: keyof AdopterFormData, value: string) {
    setAdopter((prev) => ({ ...prev, [key]: value }));
    setAdopterErrors((prev) => ({ ...prev, [key]: false }));
  }

  async function handleSave() {
    if (!chip) {
      return;
    }
    const nextPetErrors = canEditPet ? validatePetForm(pet, { requireSpayedNeutered: false }) : {};
    const nextAdopterErrors = canEditOwner ? validateAdopterForm(adopter) : {};
    setPetErrors(nextPetErrors);
    setAdopterErrors(nextAdopterErrors);
    if (Object.keys(nextPetErrors).length || Object.keys(nextAdopterErrors).length) {
      return;
    }
    setSubmitting(true);
    try {
      await onSave(chip, {
        originalPet,
        pet: canEditPet ? pet : null,
        adopter: canEditOwner ? adopter : null,
      });
      onClose();
    } catch {
      // Caller surfaces the toast; keep the modal open so nothing typed is lost.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} size="wide">
      <ModalTitle>Edit chip {chip.id}</ModalTitle>

      {canEditPet && (
        <>
          <div className={styles.sectionTitle}>Pet Information</div>
          <PetFormFields pet={pet} errors={petErrors} onChange={updatePet} hideSpayedNeutered />
        </>
      )}

      {canEditOwner && (
        <>
          <div className={styles.sectionTitle}>Owner</div>
          <OwnerFormFields adopter={adopter} errors={adopterErrors} onChange={updateAdopter} emailLocked />
        </>
      )}

      <ModalActions>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleSave} disabled={submitting}>
          {submitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
